import {
  useRef,
  useEffect,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { motion } from "framer-motion";

type RevealDirection = "up" | "down" | "left" | "right";

const PRELOAD_PX = 200;
const FALLBACK_REVEAL_MS = 600;

function isInsideViewport(el: Element) {
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return false;
  const vh = window.innerHeight || document.documentElement.clientHeight;
  const vw = window.innerWidth || document.documentElement.clientWidth;
  return (
    rect.bottom >= -PRELOAD_PX &&
    rect.top <= vh + PRELOAD_PX &&
    rect.right >= 0 &&
    rect.left <= vw
  );
}

function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** 进入视口后再淡入并位移；带同步首屏检测 + 兜底显示，避免极端情况下卡在 opacity:0。 */
export default function ScrollReveal({
  children,
  className = "",
  direction = "up",
  delay = 0,
  duration = 0.5,
  distance = 24,
  once = true,
  threshold = 0.01,
  ...rest
}: {
  children: ReactNode;
  className?: string;
  direction?: RevealDirection;
  delay?: number;
  duration?: number;
  distance?: number;
  once?: boolean;
  threshold?: number;
  id?: string;
  style?: CSSProperties;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const reduceMotion = prefersReducedMotion();
  const [isInView, setIsInView] = useState(reduceMotion);

  useEffect(() => {
    if (reduceMotion) {
      setIsInView(true);
      return;
    }

    const el = ref.current;
    if (!el) {
      setIsInView(true);
      return;
    }

    let revealed = false;
    const reveal = () => {
      if (revealed) return;
      revealed = true;
      setIsInView(true);
    };

    if (isInsideViewport(el)) {
      reveal();
      if (once) return;
    }

    let observer: IntersectionObserver | null = null;
    if (typeof window !== "undefined" && "IntersectionObserver" in window) {
      try {
        observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              reveal();
              if (once) observer?.disconnect();
            } else if (!once) {
              setIsInView(false);
              revealed = false;
            }
          },
          {
            threshold,
            rootMargin: `${PRELOAD_PX}px 0px ${PRELOAD_PX}px 0px`,
          }
        );
        observer.observe(el);
      } catch {
        reveal();
      }
    } else {
      reveal();
    }

    const fallback = window.setTimeout(reveal, FALLBACK_REVEAL_MS);

    return () => {
      observer?.disconnect();
      window.clearTimeout(fallback);
    };
  }, [once, threshold, reduceMotion]);

  const dirMap: Record<RevealDirection, { x: number; y: number }> = {
    up: { x: 0, y: distance },
    down: { x: 0, y: -distance },
    left: { x: distance, y: 0 },
    right: { x: -distance, y: 0 },
  };

  const offset = dirMap[direction];

  if (reduceMotion) {
    return (
      <div ref={ref} className={className} {...rest}>
        {children}
      </div>
    );
  }

  // 外层 wrapper 负责占位 + 裁切，承接 className/style/id；
  // 内层 motion.div 才做 transform，从而避免初始 translateY/translateX
  // 影响 <html> 的 scrollWidth/scrollHeight（否则会出现右侧/底部滚动条
  // 在首帧"撑出一截又缩回"的视觉抖动）。
  const { style, ...restProps } = rest;
  return (
    <div
      ref={ref}
      className={className}
      style={{ overflow: "clip", ...style }}
      {...restProps}
    >
      <motion.div
        initial={{ opacity: 0, x: offset.x, y: offset.y }}
        animate={
          isInView
            ? { opacity: 1, x: 0, y: 0 }
            : { opacity: 0, x: offset.x, y: offset.y }
        }
        transition={{
          duration,
          delay,
          ease: [0.25, 0.46, 0.45, 0.94],
        }}
      >
        {children}
      </motion.div>
    </div>
  );
}
